import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import { Plus, FileText, CalendarBlank, Warning } from '@phosphor-icons/react';
import { format } from 'date-fns';

const CONTRACT_TYPES = ['Retainer', 'Project-Based', 'Annual Service'];
const SERVICE_TYPES = ['Water Query', 'Electricity Query', 'New Connection', 'New Account Opening', 'Payment Arrangement',
                       'Building Plans', 'Tariff Change Application', 'Billing Query', 'Value Dispute', 'Refund Request', 'Energy Audit'];

const AdminContracts = () => {
  const [contracts, setContracts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newContract, setNewContract] = useState({
    company_id: '',
    contract_type: '',
    services_covered: [],
    start_date: null,
    end_date: null,
    auto_renew: false
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const [contractsRes, companiesRes] = await Promise.all([
        api.get(`/contracts${params}`),
        api.get('/companies')
      ]);
      setContracts(contractsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newContract.start_date || !newContract.end_date) {
      toast.error('Please select start and end dates');
      return;
    }
    try {
      await api.post('/contracts', {
        ...newContract,
        start_date: format(newContract.start_date, 'yyyy-MM-dd'),
        end_date: format(newContract.end_date, 'yyyy-MM-dd')
      });
      toast.success('Contract created');
      setIsDialogOpen(false);
      setNewContract({
        company_id: '',
        contract_type: '',
        services_covered: [],
        start_date: null,
        end_date: null,
        auto_renew: false
      });
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const toggleService = (service) => {
    setNewContract(prev => ({
      ...prev,
      services_covered: prev.services_covered.includes(service)
        ? prev.services_covered.filter(s => s !== service)
        : [...prev.services_covered, service]
    }));
  };

  const getCompanyName = (companyId) => {
    return companies.find(c => c.company_id === companyId)?.company_name || 'Unknown';
  };

  return (
    <AdminLayout title="Contracts">
      <div className="space-y-6 fade-in">
        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-40 rounded-none" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Expired">Expired</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              <div className="ml-auto">
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none" data-testid="create-contract-btn">
                      <Plus className="w-4 h-4 mr-2" /> New Contract
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[600px]">
                    <DialogHeader>
                      <DialogTitle>Create New Contract</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreate} className="space-y-4 mt-4">
                      <div>
                        <Label className="text-sm font-semibold">Company *</Label>
                        <Select value={newContract.company_id} onValueChange={(v) => setNewContract({ ...newContract, company_id: v })}>
                          <SelectTrigger className="rounded-none mt-1" data-testid="company-select">
                            <SelectValue placeholder="Select company" />
                          </SelectTrigger>
                          <SelectContent>
                            {companies.map((c) => (
                              <SelectItem key={c.company_id} value={c.company_id}>{c.company_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-semibold">Contract Type *</Label>
                        <Select value={newContract.contract_type} onValueChange={(v) => setNewContract({ ...newContract, contract_type: v })}>
                          <SelectTrigger className="rounded-none mt-1" data-testid="type-select">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {CONTRACT_TYPES.map((t) => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-semibold">Start Date *</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start rounded-none mt-1 text-left font-normal">
                                <CalendarBlank className="w-4 h-4 mr-2" />
                                {newContract.start_date ? format(newContract.start_date, 'PPP') : 'Pick a date'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={newContract.start_date}
                                onSelect={(date) => setNewContract({ ...newContract, start_date: date })}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <Label className="text-sm font-semibold">End Date *</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start rounded-none mt-1 text-left font-normal">
                                <CalendarBlank className="w-4 h-4 mr-2" />
                                {newContract.end_date ? format(newContract.end_date, 'PPP') : 'Pick a date'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={newContract.end_date}
                                onSelect={(date) => setNewContract({ ...newContract, end_date: date })}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-semibold mb-2 block">Services Covered</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {SERVICE_TYPES.map((service) => (
                            <div key={service} className="flex items-center space-x-2">
                              <Checkbox
                                id={service}
                                checked={newContract.services_covered.includes(service)}
                                onCheckedChange={() => toggleService(service)}
                              />
                              <label htmlFor={service} className="text-sm text-[#4B5563]">{service}</label>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="auto_renew"
                          checked={newContract.auto_renew}
                          onCheckedChange={(checked) => setNewContract({ ...newContract, auto_renew: checked })}
                        />
                        <label htmlFor="auto_renew" className="text-sm font-medium">Auto-renew contract</label>
                      </div>

                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-none">
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none" data-testid="submit-contract-btn">
                          Create Contract
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contracts Table */}
        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-8 h-8 border-4 border-[#0055FF] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : contracts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr className="border-b border-[#E5E7EB]">
                      <th className="text-left py-3 px-4">Contract</th>
                      <th className="text-left py-3 px-4">Company</th>
                      <th className="text-left py-3 px-4">Period</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">Renewal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.map((contract) => (
                      <tr key={contract.contract_id} className="border-b border-[#E5E7EB] hover:bg-[#F8F9FA]" data-testid={`contract-row-${contract.contract_id}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#0055FF]/10 flex items-center justify-center">
                              <FileText className="w-5 h-5 text-[#0055FF]" />
                            </div>
                            <div>
                              <p className="font-medium text-[#0A192F]">{contract.contract_type}</p>
                              <p className="text-xs text-[#4B5563]">{contract.services_covered?.length || 0} services</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-[#4B5563]">{getCompanyName(contract.company_id)}</td>
                        <td className="py-3 px-4 text-[#4B5563] text-sm">
                          {new Date(contract.start_date).toLocaleDateString()} - {new Date(contract.end_date).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={contract.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                            {contract.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {contract.renewal_alert && (
                            <div className="flex items-center gap-2">
                              <Warning className={`w-4 h-4 ${contract.renewal_alert === '30_days' ? 'text-red-500' : 'text-yellow-500'}`} />
                              <span className="text-sm">
                                {contract.renewal_alert === '30_days' && '30 days'}
                                {contract.renewal_alert === '60_days' && '60 days'}
                                {contract.renewal_alert === '90_days' && '90 days'}
                              </span>
                            </div>
                          )}
                          {contract.auto_renew && <Badge className="ml-2 bg-blue-100 text-blue-700">Auto</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-[#D1D5DB] mx-auto mb-4" />
                <p className="text-[#4B5563]">No contracts found</p>
                <Button 
                  onClick={() => setIsDialogOpen(true)} 
                  className="mt-4 bg-[#0055FF] hover:bg-[#0044CC] rounded-none"
                >
                  <Plus className="w-4 h-4 mr-2" /> Create First Contract
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminContracts;
